import React, { useState, useEffect } from "react";
import { apiSubmitStandingMetrics } from "../../../api/backend/brandAmbassador";
import "./standingForm.scss";

export default function StandingForm({ pendingBAs, onClose, onNext }) {
  const METRICS = [
    { key: "onTimeIssueCount", label: "Be on Time" },
    { key: "dailyGoalIssueCount", label: "Meet Daily Goals" },
    { key: "respectfulIssueCount", label: "Be Respectful" },
    { key: "cleanAppearanceIssueCount", label: "Neat & Clean Attire" },
    { key: "cancelationIssueCount", label: "Responsible Cancelation" },
  ];

  const total = pendingBAs.length;
  const [currentIndex, setCurrentIndex] = useState(() => {
    const saved = localStorage.getItem("standingForm-index");
    return saved !== null ? Number(saved) : 0;
  });
  const [currentEntry, setCurrentEntry] = useState(
    pendingBAs[currentIndex] || null
  );
  const [form, setForm] = useState(getSavedForm(currentIndex));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function getSavedForm(index) {
    const data = localStorage.getItem(`standingForm-${index}`);
    if (data) return JSON.parse(data);
    return METRICS.reduce((acc, m) => ({ ...acc, [m.key]: 0 }), {});
  }

  useEffect(() => {
    setCurrentEntry(pendingBAs[currentIndex] || null);
    setForm(getSavedForm(currentIndex));
    setError(null);
    localStorage.setItem("standingForm-index", currentIndex);
  }, [currentIndex, pendingBAs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...form, [name]: value };
    setForm(updated);
    localStorage.setItem(
      `standingForm-${currentIndex}`,
      JSON.stringify(updated)
    );
  };

  const handleBack = () => {
    if (submitting) return;
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  };

  const handleNext = () => {
    if (currentIndex < total - 1) setCurrentIndex((i) => i + 1);
  };

  const handleFinish = async () => {
    setSubmitting(true);
    setError(null);
  
    try {
      // build payload: array of { eventId, onTimeIssueCount, … }
      const entries = pendingBAs.map((entry, idx) => {
        const saved = JSON.parse(localStorage.getItem(`standingForm-${idx}`)) || {};
        return {
          eventId: entry.id,
          onTimeIssueCount:        Number(saved.onTimeIssueCount        || 0),
          dailyGoalIssueCount:     Number(saved.dailyGoalIssueCount     || 0),
          respectfulIssueCount:    Number(saved.respectfulIssueCount    || 0),
          cleanAppearanceIssueCount: Number(saved.cleanAppearanceIssueCount || 0),
          cancelationIssueCount:   Number(saved.cancelationIssueCount   || 0),
        };
      });
  
      // send single batch to API
      const response = await apiSubmitStandingMetrics(entries);
      const { results } = response.data; // [{ eventId, historyId }, ...]
  
      // clear storage
      pendingBAs.forEach((_, idx) =>
        localStorage.removeItem(`standingForm-${idx}`)
      );
      localStorage.removeItem("standingForm-index");
  
      // pass created results to parent
      onNext(results);
    } catch (err) {
      console.error("Failed to submit all standing metrics:", err);
      setError("Could not save—please try again.");
    } finally {
      setSubmitting(false);
    }
  };
  

  if (!currentEntry) {
    return (
      <div className="standing-form-backdrop">
        <div className="standing-form">
          <p>All ambassadors graded!</p>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  const isLast = currentIndex === total - 1;

  return (
    <div className="standing-form-backdrop">
      <div className="standing-form">
        <h3>
          {currentIndex + 1} of {total}: Rate {currentEntry.userName}
        </h3>

        {METRICS.map((m) => (
          <div key={m.key} className="field-group">
            <label htmlFor={m.key}>{m.label}</label>
            <select
              id={m.key}
              name={m.key}
              value={form[m.key]}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value={0}>0 (No issues)</option>
              <option value={1}>1 (Issue for this metric)</option>
            </select>
          </div>
        ))}

        {error && <div className="error">{error}</div>}

        <div className="buttons">
          <button
            type="button"
            onClick={handleBack}
            disabled={submitting || currentIndex === 0}
          >
            Back
          </button>
          {!isLast && (
            <button
              type="button"
              onClick={handleNext}
              disabled={submitting}
            >
              Next
            </button>
          )}
          {isLast && (
            <button
              type="button"
              onClick={handleFinish}
              disabled={submitting}
            >
              {submitting ? "Saving…" : "Finish"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
